<template>
  <div class="relative min-h-screen bg-brand-dark text-brand-cream flex items-center justify-center px-4 pt-20 pb-16 paper-texture overflow-hidden">
    <SketchDoodle type="star" :size="16" top="16%" left="10%" motion="float" :opacity="0.45" hide-on-mobile />
    <SketchDoodle type="heart" :size="18" top="20%" right="12%" motion="wiggle" :opacity="0.4" hide-on-mobile />
    <SketchDoodle type="cloud" :size="26" bottom="16%" left="8%" motion="drift" :opacity="0.35" hide-on-mobile />

    <div class="max-w-5xl w-full grid grid-cols-1 md:grid-cols-2 gap-12 items-start relative z-10">
      <!-- Left: Contact Info & Socials -->
      <div class="flex flex-col justify-center items-center md:items-start text-center md:text-left">
        <p class="sketch-dev text-sm mb-2" aria-hidden="true">// let's talk</p>
        <h2 class="text-3xl md:text-4xl font-bold mb-2">
          Contact <span class="sketch-underline">Me</span>
        </h2>
        <span class="sketch-note mb-4 rotate-[-2deg]" aria-hidden="true">say hello ✦</span>
        <p class="text-base text-brand-muted mb-6 max-w-md">
          Let's work together! Reach out for collaborations, questions, or feedback.
        </p>
        <div class="flex gap-4 mb-4">
          <a
            href="https://www.linkedin.com/in/bisrat-teshome-6182632b4/"
            target="_blank"
            rel="noopener"
            aria-label="LinkedIn"
            class="text-brand-cream hover:text-brand-muted transition"
          >
            <i class="fab fa-linkedin text-2xl"></i>
          </a>
          <a
            href="https://t.me/b_isry"
            target="_blank"
            rel="noopener"
            aria-label="Telegram"
            class="text-brand-cream hover:text-brand-muted transition"
          >
            <i class="fab fa-telegram text-2xl"></i>
          </a>
          <a
            href="mailto:bisratt1995@gmail.com"
            aria-label="Email"
            class="text-brand-cream hover:text-brand-muted transition"
          >
            <i class="fas fa-envelope text-2xl"></i>
          </a>
        </div>
        <p class="sketch-label text-brand-muted/80" aria-hidden="true">available for work ↗</p>
      </div>

      <!-- Right: Contact Form -->
      <div class="relative">
        <SketchDoodle type="arrow" :size="30" top="-1.5rem" left="-0.5rem" motion="arrow" :opacity="0.5" hide-on-mobile />
        <form
          @submit.prevent="handleSubmit"
          class="space-y-6 bg-brand-surface/90 border border-brand-muted/40 p-8 rounded-sm shadow-lg relative"
          style="border-radius: 4px 16px 4px 16px;"
        >
          <div>
            <label for="name" class="block text-sm font-medium">Name</label>
            <input
              id="name"
              v-model="form.name"
              type="text"
              class="mt-1 block w-full bg-brand-dark text-brand-cream border border-brand-muted rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-brand-cream"
              @mouseenter="isWriting = true"
              @mouseleave="isWriting = false"
            />
            <p v-if="errors.name" class="text-red-500 text-sm mt-1">{{ errors.name }}</p>
          </div>
          <div>
            <label for="email" class="block text-sm font-medium">Email</label>
            <input
              id="email"
              v-model="form.email"
              type="email"
              class="mt-1 block w-full bg-brand-dark text-brand-cream border border-brand-muted rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-brand-cream"
              @mouseenter="isWriting = true"
              @mouseleave="isWriting = false"
            />
            <p v-if="errors.email" class="text-red-500 text-sm mt-1">{{ errors.email }}</p>
          </div>
          <div>
            <label for="message" class="block text-sm font-medium">Message</label>
            <textarea
              id="message"
              v-model="form.message"
              rows="4"
              class="mt-1 block w-full bg-brand-dark text-brand-cream border border-brand-muted rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-brand-cream"
              @mouseenter="isWriting = true"
              @mouseleave="isWriting = false"
            ></textarea>
            <p v-if="errors.message" class="text-red-500 text-sm mt-1">{{ errors.message }}</p>
          </div>
          <button
            type="submit"
            class="w-full bg-brand-dark text-brand-cream py-2 border border-brand-cream rounded-md hover:bg-brand-cream hover:text-brand-dark transition duration-300"
            @mouseenter="isHovering = true"
            @mouseleave="isHovering = false"
          >
            Send
          </button>
          <p v-if="successMessage" class="text-green-500 text-sm mt-4">{{ successMessage }}</p>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from "vue";
import SketchDoodle from "../components/SketchDoodle.vue";

export default {
  components: { SketchDoodle },
  setup() {
    const form = ref({ name: "", email: "", message: "" });
    const errors = ref({});
    const successMessage = ref("");
    const isHovering = ref(false);
    const isWriting = ref(false);

    const validateForm = () => {
      errors.value = {};
      if (!form.value.name) errors.value.name = "Name is required.";
      if (!form.value.email) {
        errors.value.email = "Email is required.";
      } else if (!/\S+@\S+\.\S+/.test(form.value.email)) {
        errors.value.email = "Email is invalid.";
      }
      if (!form.value.message) errors.value.message = "Message is required.";
      return Object.keys(errors.value).length === 0;
    };

    const handleSubmit = () => {
      if (validateForm()) {
        successMessage.value = "Thank you for reaching out! I’ll get back to you soon.";
        form.value = { name: "", email: "", message: "" };
        setTimeout(() => (successMessage.value = ""), 5000);
      }
    };

    return { form, errors, successMessage, isHovering, isWriting, handleSubmit };
  },
};
</script>
